import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Check, X, MapPin, Settings, MoonStar } from "lucide-react";
import RoosterLogo from "@/components/RoosterLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-click snooze confirmation. Triggered from the email "Snooze 1 week"
 * link. Sets active=false + snooze_until=now+7d. Scheduler auto-resumes.
 */
export default function SnoozeLocationPage() {
  const { token, subId } = useParams();
  const [status, setStatus] = useState("snoozing"); // snoozing | done | error
  const [info, setInfo] = useState({ first_name: "", location_label: "", until_human: "" });
  const [message, setMessage] = useState("");
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    (async () => {
      try {
        const r = await axios.post(`${API}/manage/${token}/subscriptions/${subId}/snooze`);
        setInfo({
          first_name: r.data?.first_name || "friend",
          location_label: r.data?.location_label || "this location",
          until_human: r.data?.until_human || "next week",
        });
        setStatus("done");
      } catch (e) {
        setMessage(e?.response?.data?.detail || "We couldn't snooze that location right now.");
        setStatus("error");
      }
    })();
  }, [token, subId]);

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <RoosterLogo size={36} />
            <div className="font-display text-2xl font-black tracking-tighter">Weathervane</div>
          </Link>
        </div>
      </header>
      <section className="max-w-2xl mx-auto px-6 lg:px-12 py-24">
        <div className="border border-black bg-white p-10" data-testid="snooze-location-card">
          {status === "snoozing" && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 border border-black bg-white mb-6">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
              <div className="ticker-label mb-3">Pausing</div>
              <h1 className="font-display text-4xl font-black tracking-tighter">Snoozing this location&hellip;</h1>
            </>
          )}

          {status === "done" && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 border border-black bg-[#FFD600] mb-6">
                <MoonStar className="w-6 h-6" />
              </div>
              <div className="ticker-label mb-3">Snoozed</div>
              <h1 className="font-display text-4xl font-black tracking-tighter">
                See you next week, {info.first_name}.
              </h1>
              <p className="mt-6 text-neutral-700 text-lg">
                <span className="inline-flex items-center gap-2">
                  <MapPin className="w-4 h-4 opacity-60" />
                  <strong>{info.location_label}</strong>
                </span>
                <br />
                is paused for 1 week and will <strong>auto-resume on {info.until_human}</strong>.
              </p>
              <p className="mt-4 text-sm text-neutral-500">
                You won&rsquo;t receive forecasts for this location until then. To resume it sooner or change anything, use the link below.
              </p>

              <div className="mt-10 pt-6 border-t border-black/10">
                <Link
                  to={`/manage/${token}`}
                  data-testid="snooze-manage-link"
                  className="btn-brutal inline-flex items-center gap-3 px-6 py-4 text-sm tracking-[0.14em] uppercase font-semibold"
                >
                  <Settings className="w-4 h-4" /> Manage my subscriptions
                </Link>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 border border-black bg-white mb-6">
                <X className="w-6 h-6" />
              </div>
              <div className="ticker-label mb-3">Hmm</div>
              <h1 className="font-display text-3xl font-black tracking-tighter">
                We couldn&rsquo;t snooze that location.
              </h1>
              <p className="mt-4 text-neutral-700">{message}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={`/manage/${token}`}
                  className="btn-brutal inline-flex items-center gap-3 px-6 py-3 text-sm tracking-[0.14em] uppercase font-semibold"
                  data-testid="snooze-error-manage-link"
                >
                  Manage subscriptions
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm tracking-[0.14em] uppercase font-semibold border border-black bg-white hover:bg-[#FFD600] transition-colors"
                >
                  Back to home
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
